import React, { FC, ReactNode } from 'react'
import { Button } from 'components/ui/Button'

// Keep this page self-contained so it can be copied as one file.
// Add a route to it wherever convenient, for example: /ui/button-demo

const pageStyle: React.CSSProperties = {
  padding: 24,
  background: '#f7f8fb',
  // minHeight: '100vh',
  color: '#080910',
  overflow: 'scroll'
}

const containerStyle: React.CSSProperties = {
  maxWidth: 1440,
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 24
}

const heroStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e6e6e6',
  borderRadius: 16,
  padding: 24,
  boxShadow: '0 8px 30px rgba(8, 9, 16, 0.06)'
}

const sectionStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e6e6e6',
  borderRadius: 16,
  padding: 24,
  boxShadow: '0 8px 30px rgba(8, 9, 16, 0.06)'
}

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: 16,
  alignItems: 'stretch'
}

const cardStyle: React.CSSProperties = {
  border: '1px solid #e6e6e6',
  borderRadius: 14,
  padding: 16,
  background: '#fcfcfd',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  minHeight: 180
}

const previewStyle: React.CSSProperties = {
  minHeight: 72,
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  alignItems: 'center'
}

const codeStyle: React.CSSProperties = {
  margin: 0,
  padding: 14,
  borderRadius: 12,
  background: '#0f172a',
  color: '#e2e8f0',
  overflowX: 'auto',
  fontSize: 13,
  lineHeight: 1.6,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word'
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 14
}

const thtdStyle: React.CSSProperties = {
  borderBottom: '1px solid #e6e6e6',
  textAlign: 'left',
  padding: '10px 8px',
  verticalAlign: 'top'
}

const badgeStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '4px 8px',
  borderRadius: 999,
  background: '#eef4ff',
  color: '#286efa',
  fontSize: 12,
  fontWeight: 600
}

const titleRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  flexWrap: 'wrap'
}

const DemoIcon = ({ iconActive }: any) => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path
      d="M9 1.5L11.317 6.196L16.5 6.952L12.75 10.607L13.635 15.767L9 13.33L4.365 15.767L5.25 10.607L1.5 6.952L6.683 6.196L9 1.5Z"
      fill={iconActive ? '#286EFA' : '#98A2B3'}
    />
  </svg>
)

const DemoSvg = ({ color = 'currentColor' }: any) => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path
      d="M3 9H15M9 3L15 9L9 15"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Section: FC<{ title: string; description?: string; children: ReactNode }> = ({
  title,
  description,
  children
}) => (
  <section style={sectionStyle}>
    <div style={{ marginBottom: 20 }}>
      <h2 style={{ margin: '0 0 8px', fontSize: 24 }}>{title}</h2>
      {description ? <p style={{ margin: 0, color: '#667085' }}>{description}</p> : null}
    </div>
    {children}
  </section>
)

const DemoCard: FC<{
  title: string
  code: string
  children: ReactNode
  note?: string
}> = ({ title, code, children, note }) => (
  <div style={cardStyle}>
    <div>
      <h3 style={{ margin: '0 0 6px', fontSize: 18 }}>{title}</h3>
      {note ? <p style={{ margin: 0, color: '#667085', fontSize: 14 }}>{note}</p> : null}
    </div>
    <div style={previewStyle}>{children}</div>
    <pre style={codeStyle}>
      <code>{code}</code>
    </pre>
  </div>
)

const ButtonDocsDemoPage: FC = () => {
  return (
    <div style={pageStyle}>
      <div style={containerStyle}>
        <section style={heroStyle}>
          <div style={titleRowStyle}>
            <div>
              <h1 style={{ margin: '0 0 8px', fontSize: 32 }}>Button</h1>
              <p style={{ margin: 0, color: '#667085', maxWidth: 920 }}>
                Live demo of the standard Button API. Each block renders real buttons and shows
                copy-paste TSX right below it, similar to component documentation pages.
              </p>
            </div>
            <span style={badgeStyle}>components/ui/Button</span>
          </div>

          <div style={{ overflowX: 'auto', marginTop: 20 }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thtdStyle}>Prop</th>
                  <th style={thtdStyle}>Type</th>
                  <th style={thtdStyle}>Default</th>
                  <th style={thtdStyle}>Values</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={thtdStyle}>variant</td>
                  <td style={thtdStyle}>ButtonVariantType</td>
                  <td style={thtdStyle}>contained</td>
                  <td style={thtdStyle}>text, contained, outlined, icon</td>
                </tr>
                <tr>
                  <td style={thtdStyle}>color</td>
                  <td style={thtdStyle}>ButtonColorType</td>
                  <td style={thtdStyle}>primary</td>
                  <td style={thtdStyle}>primary, second, danger, gradientReward, success</td>
                </tr>
                <tr>
                  <td style={thtdStyle}>size</td>
                  <td style={thtdStyle}>ButtonSizeType</td>
                  <td style={thtdStyle}>md</td>
                  <td style={thtdStyle}>sm, md, lg</td>
                </tr>
                <tr>
                  <td style={thtdStyle}>figure</td>
                  <td style={thtdStyle}>ButtonFigureType</td>
                  <td style={thtdStyle}>—</td>
                  <td style={thtdStyle}>rounded, roundedSquere</td>
                </tr>
                <tr>
                  <td style={thtdStyle}>fill</td>
                  <td style={thtdStyle}>boolean</td>
                  <td style={thtdStyle}>false</td>
                  <td style={thtdStyle}>true, false</td>
                </tr>
                <tr>
                  <td style={thtdStyle}>disabled</td>
                  <td style={thtdStyle}>boolean</td>
                  <td style={thtdStyle}>false</td>
                  <td style={thtdStyle}>true, false</td>
                </tr>
                <tr>
                  <td style={thtdStyle}>to</td>
                  <td style={thtdStyle}>string</td>
                  <td style={thtdStyle}>—</td>
                  <td style={thtdStyle}>switches component to react-router Link</td>
                </tr>
                <tr>
                  <td style={thtdStyle}>Icon</td>
                  <td style={thtdStyle}>component</td>
                  <td style={thtdStyle}>—</td>
                  <td style={thtdStyle}>custom icon renderer</td>
                </tr>
                <tr>
                  <td style={thtdStyle}>svg</td>
                  <td style={thtdStyle}>component</td>
                  <td style={thtdStyle}>—</td>
                  <td style={thtdStyle}>custom svg renderer with color prop</td>
                </tr>
                <tr>
                  <td style={thtdStyle}>optionsStyle</td>
                  <td style={thtdStyle}>object</td>
                  <td style={thtdStyle}>—</td>
                  <td style={thtdStyle}>fine-tunes text and icon variants</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <Section
          title="Variants"
          description="Base visual behavior for the same action. Start with these before combining other props."
        >
          <div style={gridStyle}>
            <DemoCard title="Contained" code={`<Button label="Contained" variant="contained" />`}>
              <Button label="Contained" variant="contained" />
            </DemoCard>

            <DemoCard title="Outlined" code={`<Button label="Outlined" variant="outlined" />`}>
              <Button label="Outlined" variant="outlined" />
            </DemoCard>

            <DemoCard title="Text" code={`<Button label="Text" variant="text" />`}>
              <Button label="Text" variant="text" />
            </DemoCard>

            <DemoCard
              title="Icon"
              code={`<Button variant="icon" Icon={DemoIcon} aria-label="Favorite" />`}
              note="Icon-only action. Add aria-label when there is no visible text."
            >
              <Button variant="icon" Icon={DemoIcon} aria-label="Favorite" />
            </DemoCard>
          </div>
        </Section>

        <Section
          title="Colors"
          description="The same variant rendered with all supported color tokens."
        >
          <div style={gridStyle}>
            <DemoCard
              title="Contained colors"
              code={`<>
  <Button label="Primary" color="primary" />
  <Button label="Second" color="second" />
  <Button label="Danger" color="danger" />
  <Button label="Reward" color="gradientReward" />
  <Button label="Success" color="success" />
</>`}
            >
              <Button label="Primary" color="primary" />
              <Button label="Second" color="second" />
              <Button label="Danger" color="danger" />
              <Button label="Reward" color="gradientReward" />
              <Button label="Success" color="success" />
            </DemoCard>

            <DemoCard
              title="Outlined colors"
              code={`<>
  <Button label="Primary" variant="outlined" color="primary" />
  <Button label="Second" variant="outlined" color="second" />
  <Button label="Danger" variant="outlined" color="danger" />
  <Button label="Reward" variant="outlined" color="gradientReward" />
  <Button label="Success" variant="outlined" color="success" />
</>`}
            >
              <Button label="Primary" variant="outlined" color="primary" />
              <Button label="Second" variant="outlined" color="second" />
              <Button label="Danger" variant="outlined" color="danger" />
              <Button label="Reward" variant="outlined" color="gradientReward" />
              <Button label="Success" variant="outlined" color="success" />
            </DemoCard>

            <DemoCard
              title="Text colors"
              code={`<>
  <Button label="Primary" variant="text" color="primary" />
  <Button label="Second" variant="text" color="second" />
  <Button label="Danger" variant="text" color="danger" />
  <Button label="Reward" variant="text" color="gradientReward" />
  <Button label="Success" variant="text" color="success" />
</>`}
            >
              <Button label="Primary" variant="text" color="primary" />
              <Button label="Second" variant="text" color="second" />
              <Button label="Danger" variant="text" color="danger" />
              <Button label="Reward" variant="text" color="gradientReward" />
              <Button label="Success" variant="text" color="success" />
            </DemoCard>

            <DemoCard
              title="Icon colors"
              code={`<>
  <Button variant="icon" color="primary" Icon={DemoIcon} aria-label="Primary icon" />
  <Button variant="icon" color="second" Icon={DemoIcon} aria-label="Second icon" />
  <Button variant="icon" color="danger" Icon={DemoIcon} aria-label="Danger icon" />
  <Button variant="icon" color="gradientReward" Icon={DemoIcon} aria-label="Reward icon" />
  <Button variant="icon" color="success" Icon={DemoIcon} aria-label="Success icon" />
</>`}
            >
              <Button variant="icon" color="primary" Icon={DemoIcon} aria-label="Primary icon" />
              <Button variant="icon" color="second" Icon={DemoIcon} aria-label="Second icon" />
              <Button variant="icon" color="danger" Icon={DemoIcon} aria-label="Danger icon" />
              <Button
                variant="icon"
                color="gradientReward"
                Icon={DemoIcon}
                aria-label="Reward icon"
              />
              <Button variant="icon" color="success" Icon={DemoIcon} aria-label="Success icon" />
            </DemoCard>
          </div>
        </Section>

        <Section
          title="Sizes"
          description="Use size to control button density without changing the semantic meaning."
        >
          <div style={gridStyle}>
            <DemoCard
              title="All sizes"
              code={`<>
  <Button label="Small" size="sm" />
  <Button label="Medium" size="md" />
  <Button label="Large" size="lg" />
</>`}
            >
              <Button label="Small" size="sm" />
              <Button label="Medium" size="md" />
              <Button label="Large" size="lg" />
            </DemoCard>

            <DemoCard
              title="Outlined + sizes"
              code={`<>
  <Button label="Small" size="sm" variant="outlined" />
  <Button label="Medium" size="md" variant="outlined" />
  <Button label="Large" size="lg" variant="outlined" />
</>`}
            >
              <Button label="Small" size="sm" variant="outlined" />
              <Button label="Medium" size="md" variant="outlined" />
              <Button label="Large" size="lg" variant="outlined" />
            </DemoCard>
          </div>
        </Section>

        <Section
          title="Figure and width"
          description="These props change the button shape and layout behavior."
        >
          <div style={gridStyle}>
            <DemoCard
              title="Figure"
              code={`<>
  <Button label="Rounded" figure="rounded" />
  <Button label="Rounded square" figure="roundedSquere" />
</>`}
            >
              <Button label="Rounded" figure="rounded" />
              <Button label="Rounded square" figure="roundedSquere" />
            </DemoCard>

            <DemoCard
              title="Fill"
              code={`<div style={{ width: 320 }}>
  <Button label="Full width" fill />
</div>`}
              note="Works only when the parent container has a constrained width."
            >
              <div style={{ width: 320 }}>
                <Button label="Full width" fill />
              </div>
            </DemoCard>
          </div>
        </Section>

        <Section
          title="With icons and svg"
          description="Two supported visual APIs: Icon and svg. Use whichever matches the existing asset implementation."
        >
          <div style={gridStyle}>
            <DemoCard title="Label + Icon" code={`<Button label="Save" Icon={DemoIcon} />`}>
              <Button label="Save" Icon={DemoIcon} />
            </DemoCard>

            <DemoCard
              title="Label + active Icon"
              code={`<Button label="Saved" Icon={DemoIcon} iconActive />`}
            >
              <Button label="Saved" Icon={DemoIcon} iconActive />
            </DemoCard>

            <DemoCard
              title="Label + svg"
              code={`<Button label="Next" svg={DemoSvg} />`}
              note="Useful when the svg component expects the color prop passed by Button."
            >
              <Button label="Next" svg={DemoSvg} />
            </DemoCard>

            <DemoCard
              title="Icon variant with background and padding"
              code={`<Button
  variant="icon"
  color="second"
  Icon={DemoIcon}
  aria-label="Settings"
  optionsStyle={{
    variant: {
      icon: {
        showBg: true,
        addPadding: true
      }
    }
  }}
/>`}
            >
              <Button
                variant="icon"
                color="second"
                Icon={DemoIcon}
                aria-label="Settings"
                optionsStyle={{
                  variant: {
                    icon: {
                      showBg: true,
                      addPadding: true
                    }
                  }
                }}
              />
            </DemoCard>
          </div>
        </Section>

        <Section
          title="Disabled and link mode"
          description="The component supports both native button mode and react-router Link mode."
        >
          <div style={gridStyle}>
            <DemoCard
              title="Disabled"
              code={`<>
  <Button label="Disabled contained" disabled />
  <Button label="Disabled outlined" variant="outlined" disabled />
  <Button label="Disabled text" variant="text" disabled />
  <Button variant="icon" Icon={DemoIcon} aria-label="Disabled icon" disabled />
</>`}
            >
              <Button label="Disabled contained" disabled />
              <Button label="Disabled outlined" variant="outlined" disabled />
              <Button label="Disabled text" variant="text" disabled />
              <Button variant="icon" Icon={DemoIcon} aria-label="Disabled icon" disabled />
            </DemoCard>

            <DemoCard
              title="Link mode"
              code={`<>
  <Button label="Go to sign up" to="/sign-up" />
  <Button label="Find jobs" to="/find-jobs" variant="outlined" />
</>`}
              note="When to is provided, Button renders react-router Link instead of a native button."
            >
              <Button label="Go to sign up" to="/sign-up" />
              <Button label="Find jobs" to="/find-jobs" variant="outlined" />
            </DemoCard>
          </div>
        </Section>

        <Section
          title="optionsStyle examples"
          description="These combinations expose the special-case styling hidden in buttonStyleSelector."
        >
          <div style={gridStyle}>
            <DemoCard
              title="Text variant with default no padding"
              code={`<Button label="Text action" variant="text" />`}
            >
              <Button label="Text action" variant="text" />
            </DemoCard>

            <DemoCard
              title="Text variant with addPaddings"
              code={`<Button
  label="Text action"
  variant="text"
  optionsStyle={{
    variant: {
      text: {
        addPaddings: true
      }
    }
  }}
/>`}
            >
              <Button
                label="Text action"
                variant="text"
                optionsStyle={{
                  variant: {
                    text: {
                      addPaddings: true
                    }
                  }
                }}
              />
            </DemoCard>

            <DemoCard
              title="Icon variant without background"
              code={`<Button variant="icon" Icon={DemoIcon} aria-label="Plain icon" />`}
            >
              <Button variant="icon" Icon={DemoIcon} aria-label="Plain icon" />
            </DemoCard>

            <DemoCard
              title="Icon variant with background only"
              code={`<Button
  variant="icon"
  color="second"
  Icon={DemoIcon}
  aria-label="Icon with background"
  optionsStyle={{
    variant: {
      icon: {
        showBg: true
      }
    }
  }}
/>`}
            >
              <Button
                variant="icon"
                color="second"
                Icon={DemoIcon}
                aria-label="Icon with background"
                optionsStyle={{
                  variant: {
                    icon: {
                      showBg: true
                    }
                  }
                }}
              />
            </DemoCard>
          </div>
        </Section>

        <Section
          title="Useful combinations"
          description="A few realistic presets you can reuse in the app."
        >
          <div style={gridStyle}>
            <DemoCard
              title="Primary call to action"
              code={`<Button label="Create account" size="lg" color="primary" />`}
            >
              <Button label="Create account" size="lg" color="primary" />
            </DemoCard>

            <DemoCard
              title="Secondary neutral action"
              code={`<Button label="Back" variant="outlined" color="second" />`}
            >
              <Button label="Back" variant="outlined" color="second" />
            </DemoCard>

            <DemoCard title="Danger action" code={`<Button label="Delete" color="danger" />`}>
              <Button label="Delete" color="danger" />
            </DemoCard>

            <DemoCard
              title="Reward action"
              code={`<Button label="Claim reward" color="gradientReward" />`}
            >
              <Button label="Claim reward" color="gradientReward" />
            </DemoCard>
          </div>
        </Section>
      </div>
    </div>
  )
}

export default ButtonDocsDemoPage
